<script setup>
import { Head, Link, useForm } from "@inertiajs/inertia-vue3";
import Layout from "../Layouts/AdminLayout.vue";
import Button from "primevue/button";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {onBeforeMount, ref, watch} from "vue";
import axios from "axios";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Dropdown from "primevue/dropdown";
import { useToast } from "primevue/usetoast";
import {debounce} from "../utils/debounce";

const props = defineProps([
    'flash',
    'roleOptions',
    'companyOptions',
    'isSuperAdmin',
]);

const toast = useToast()
const users = ref([])
const totalRecords = ref(0)
const lastPage = ref(1)
const loading = ref(false)
const userDelete = ref({})
const userDeleteForm = useForm({})
const search = ref('')
const selectedRole = ref(null)
const selectedCompany = ref(null)
const datatable = ref(null)

const getParams = (page = 1) => {
    const params = {
        page: page
    }

    if (search.value) {
        params['s'] = search.value
    }

    if (selectedRole.value) {
        params['role'] = selectedRole.value
    }

    if (props.isSuperAdmin && selectedCompany.value) {
        params['company_id'] = selectedCompany.value
    }

    return params
}

const onPage = async (e) => {
    loading.value = true

    const { data } = await axios.get('/ajax/admin/users', {
        params: getParams(e.page + 1)
    })

    loadDatatable(data)
}

onBeforeMount( async () => {
    await fetchUsers()
})

const fetchUsers = async () => {
    loading.value = true

    const { data } = await axios.get('/ajax/admin/users', {
        params: getParams()
    })

    loadDatatable(data)
}

const showDeleteDialog = (user) => {
    userDelete.value = user
}

const deleteUser = () => {
    userDeleteForm.delete(`/admin/users/${userDelete.value.uuid}`, {
        preserveState: false,

        onSuccess: () => {
            toast.add({
                severity: "success",
                summary: "Benutzer gelöscht",
                life: 5000,
            });
        },
    })
}

const debounceSearch = debounce(async () => {
    datatable.value.d_first = 0
    await fetchUsers()
}, 300)

watch([selectedRole, selectedCompany], async () => {
    datatable.value.d_first = 0
    await fetchUsers()
})

const loadDatatable = (data) => {
    users.value = data.users.data
    totalRecords.value = data.users.total

    loading.value = false
}
</script>

<template>
    <Layout :flash="flash">
        <Head title="Admin Benutzer" />

        <div class="py-7 text-sm">
            <div class="px-14 mb-2">
                <div class="mb-4">
                    <div class="flex justify-end">
                        <Button
                            asChild
                            v-slot="slotProps"
                            severity="success">
                            <Link href="/admin/users/create" :class="slotProps.class">Hinzufügen</Link>
                        </Button>
                    </div>
                </div>

                <div class="flex gap-4 mb-4 items-end">
                    <div class="flex-grow">
                        <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Suche</label>
                        <IconField iconPosition="right">
                            <InputIcon class="pi pi-search"></InputIcon>

                            <InputText v-model="search"
                                       class="w-full !h-[42px]"
                                       placeholder="Email, Vorname, Nachname"
                                       @input="debounceSearch"></InputText>
                        </IconField>
                    </div>

                    <div class="w-64 shrink-0">
                        <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Rolle</label>
                        <Dropdown v-model="selectedRole"
                                  :options="roleOptions"
                                  optionLabel="label"
                                  optionValue="value"
                                  placeholder="Alle Rollen"
                                  class="w-full !h-[42px] flex items-center"
                                  showClear />
                    </div>

                    <div v-if="isSuperAdmin" class="w-64 shrink-0">
                        <label class="block font-bold mb-1 text-xs uppercase text-gray-500">Unternehmen</label>
                        <Dropdown v-model="selectedCompany"
                                  :options="companyOptions"
                                  optionLabel="label"
                                  optionValue="value"
                                  placeholder="Alle Unternehmen"
                                  class="w-full !h-[42px] flex items-center"
                                  showClear
                                  filter />
                    </div>
                </div>
            </div>

            <div>
                <DataTable :value="users"
                           :totalRecords="totalRecords"
                           :loading="loading"
                           :rows="10"
                           dataKey="id"
                           ref="datatable"
                           @page="onPage($event)"
                           lazy
                           paginator>
                    <Column header="Email" field="email"></Column>

                    <Column header="Rolle" field="roleName"></Column>

                    <Column header="">
                        <template #body="props">
                            <div class="flex justify-center gap-x-3">
                                <Link :href="`/admin/users/${props.data.uuid}/edit`">
                                    <i class="pi pi-file-edit cursor-pointer" />
                                </Link>


                                <i class="pi pi-trash cursor-pointer text-danger"
                                   @click="showDeleteDialog(props.data)" />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <Dialog v-model:visible="userDelete.id"
                class="max-w-[469px]"
                :draggable="false"
                :closable="false"
                modal>
            <h3 class="poppins font-semibold text-2xl text-[#101828] mb-3">Bist du sicher, dass du {{ userDelete.email }} löschen möchtest?</h3>

            <template #footer>
                <div class="flex w-full gap-3">
                    <Button label="Abbrechen"
                            severity="secondary"
                            class="text-[#2B2B2B] w-full basis-6/12"
                            @click="userDelete = {}"
                            size="small"
                            outlined />

                    <Button label="Abmelden"
                            :disabled="userDeleteForm.processing"
                            :loading="userDeleteForm.processing"
                            @click="deleteUser"
                            severity="danger w-full basis-6/12"
                            size="small" />
                </div>
            </template>
        </Dialog>
    </Layout>
</template>
